<template>
  <div class="line-card pa-3 bg-white rounded-lg border-light">
    <!-- 1. Encabezado de la Tarjeta de Línea -->
    <div class="d-flex align-center justify-space-between mb-2 pb-2 border-bottom">
      <div class="d-flex align-center flex-wrap gap-2">
        <span class="line-badge font-weight-bold grey--text text--darken-2">#{{ index + 1 }}</span>
        <span class="text-subtitle-2 font-weight-black grey--text text--darken-4">
          {{ line.EpdProduct }}
        </span>
        <v-chip v-if="line.EpdSapCode" x-small color="grey lighten-3" class="font-weight-medium">
          Art. SAP: {{ line.EpdSapCode }}
        </v-chip>
        <span class="caption font-weight-bold grey--text text--darken-3" v-if="line.EpdBrand">
          Marca: {{ line.EpdBrand }}
        </span>
        <v-chip
          v-if="line.PrsCustomerOriginID === 'Sin Cliente'"
          x-small
          color="indigo lighten-5"
          text-color="indigo darken-4"
          class="font-weight-medium"
        >
          Origen: Sin Cliente
        </v-chip>
      </div>

      <div class="d-flex align-center gap-2">
        <v-chip x-small class="font-weight-bold" color="blue lighten-5" text-color="blue darken-4">
          Planificado
        </v-chip>

        <!-- Botones de Acción de Línea -->
        <div v-if="!isView" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" color="primary" @click="$emit('edit', line)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Editar Línea</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" color="teal darken-2" @click="$emit('add-similar', line)">
                <v-icon small>mdi-content-duplicate</v-icon>
              </v-btn>
            </template>
            <span>Agregar Similar</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" color="indigo" @click="$emit('duplicate', line)">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <span>Duplicar Línea</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" color="error" @click="$emit('delete', line)">
                <v-icon small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <span>Eliminar Línea</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- 2. Información Comercial / Chips Discretos -->
    <div class="d-flex align-center flex-wrap gap-2 mb-2">
      <div class="info-chip">
        <span class="info-chip-label">Empaque:</span>
        <span class="info-chip-val">{{ line.EpdPackaging || '-' }}</span>
      </div>

      <div class="info-chip">
        <span class="info-chip-label">Capacidad:</span>
        <span class="info-chip-val font-weight-bold">{{ line.EpdCapacity || 0 }} TM/FCL</span>
        <v-tooltip bottom v-if="line.EpdIsCapacityException === 1">
          <template v-slot:activator="{ on, attrs }">
            <v-chip x-small color="amber lighten-4" text-color="amber darken-4" class="ml-1 font-weight-bold" v-bind="attrs" v-on="on">
              ⚡ Excepción
            </v-chip>
          </template>
          <span>{{ line.EpdCapacityExceptionReason || 'Capacidad manual autorizada' }}</span>
        </v-tooltip>
      </div>

      <div class="info-chip">
        <span class="info-chip-label">Tipo:</span>
        <span class="info-chip-val">{{ line.EpdCommercialType === 2 ? 'Retail' : 'Bulk' }}</span>
      </div>

      <div class="info-chip">
        <span class="info-chip-label">Incoterm:</span>
        <span class="info-chip-val font-weight-bold">{{ getIncotermName(line.TypeIncoterm) }}</span>
      </div>

      <div class="info-chip" v-if="line.TypeIncoterm !== 1 && line.EpdPriceDifferential">
        <span class="info-chip-label">Diferencial:</span>
        <span class="info-chip-val font-weight-bold amber--text text--darken-4">${{ formatNumber(line.EpdPriceDifferential, 4) }}/kg</span>
      </div>

      <div class="info-chip" v-if="line.EpdMarketPrice">
        <span class="info-chip-label">P. Mercado:</span>
        <span class="info-chip-val">${{ formatNumber(line.EpdMarketPrice, 4) }}/kg</span>
      </div>

      <div class="info-chip">
        <span class="info-chip-label">Puerto/Destino:</span>
        <span class="info-chip-val">{{ line.EpdPortDestination || '-' }}</span>
      </div>

      <div class="info-chip">
        <span class="info-chip-label">Pago:</span>
        <span class="info-chip-val">{{ line.EpdPaymentCondition || '-' }}</span>
      </div>

      <div class="info-chip" v-if="line.EpdExecutive">
        <span class="info-chip-label">Ejecutivo:</span>
        <span class="info-chip-val">{{ line.EpdExecutive }}</span>
      </div>
    </div>

    <!-- 3. Fila de Indicadores Numéricos -->
    <div class="pa-2 rounded-lg bg-grey-lighten-5 border-light d-flex align-center justify-space-between flex-wrap gap-3">
      <!-- TM Programadas -->
      <div class="metric-group">
        <div class="metric-label">TM PROGRAMADAS</div>
        <div class="metric-val primary--text font-weight-black">
          {{ formatNumber(line.EpdProgrammedTons, 2) }} TM
        </div>
      </div>

      <!-- FCL Programados -->
      <div class="metric-group">
        <div class="metric-label">FCL PROGRAMADOS</div>
        <div class="metric-val font-weight-bold grey--text text--darken-3">
          {{ formatNumber(line.EpdProgrammedFcl, 2) }} FCL
        </div>
      </div>

      <!-- Precio Pactado -->
      <div class="metric-group">
        <div class="metric-label">PRECIO PACTADO</div>
        <div class="metric-val font-weight-bold grey--text text--darken-4">
          ${{ formatNumber(line.EpdPriceAgreed, 4) }} /kg
        </div>
      </div>

      <!-- Precio FOB Eq -->
      <div class="metric-group">
        <div class="metric-label">PRECIO FOB EQ</div>
        <div class="metric-val font-weight-bold grey--text text--darken-4">
          ${{ formatNumber(line.EpdPriceFobEquivalent, 4) }} /kg
        </div>
      </div>

      <!-- Importe Contractual -->
      <div class="metric-group">
        <div class="metric-label">IMPORTE CONTRACTUAL</div>
        <div class="metric-val font-weight-bold grey--text text--darken-3">
          ${{ formatNumber(line.EpdAmountContract, 2) }}
        </div>
      </div>

      <!-- Importe FOB Eq Total -->
      <div class="metric-group">
        <div class="metric-label">IMPORTE FOB EQ TOTAL</div>
        <div class="metric-val green--text text--darken-4 font-weight-black text-subtitle-2">
          ${{ formatNumber(line.EpdAmountFobEquivalent, 2) }}
        </div>
      </div>
    </div>

    <!-- Observación de Línea (si existe) -->
    <div v-if="line.EpdObservation" class="mt-2 text-caption grey--text text--darken-2 font-italic">
      <v-icon x-small color="grey">mdi-information-outline</v-icon>
      {{ line.EpdObservation }}
    </div>
  </div>
</template>

<script>
import { formatNumber, getIncotermName } from "../utils/CommercialProgrammingCalculations";

export default {
  name: "CommercialLineCard",
  props: {
    line: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    isView: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatNumber,
    getIncotermName,
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.line-card {
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}
.line-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}
.line-badge {
  font-size: 12px;
  background-color: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}
.info-chip {
  font-size: 11px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.info-chip-label {
  color: #64748b;
  font-weight: 600;
}
.info-chip-val {
  color: #1e293b;
}
.metric-group {
  display: flex;
  flex-direction: column;
}
.metric-label {
  font-size: 9px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.4px;
}
.metric-val {
  font-size: 12px;
}
</style>
